/**
 * CycleManager — List and manage performance cycles.
 * Admin-only view. Creation is handled by CycleModal.
 */
import React, { useState } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import CalendarRange from 'lucide-react/dist/esm/icons/calendar-range';
import Play from 'lucide-react/dist/esm/icons/play';
import Pause from 'lucide-react/dist/esm/icons/pause';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Repeat from 'lucide-react/dist/esm/icons/repeat';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import { Button, IconButton } from '../../../components/ui';
import { useKPIStore } from '../../../stores/kpiStore';
import { useUIStore } from '../../../stores/uiStore';
import { getTranslation } from '../../../i18n';
import CycleModal from './CycleModal';

const CycleManager: React.FC = () => {
  const { cycles, createCycle, updateCycleStatus, setActiveCycleId, deleteCycle } = useKPIStore();
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const STATUS_CONFIG: Record<string, { label: string; color: string; next?: string; nextLabel?: string; nextIcon?: any }> = {
    draft: { label: t('perfCycleManagerStatusDraft'), color: 'text-content-tertiary bg-surface-secondary border-default', next: 'active', nextLabel: t('perfCycleManagerBtnActivate'), nextIcon: Play },
    active: { label: t('perfCycleManagerStatusActive'), color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', next: 'review', nextLabel: t('perfCycleManagerBtnStartReview'), nextIcon: Pause },
    review: { label: t('perfCycleManagerStatusInReview'), color: 'text-amber-400 bg-amber-400/10 border-amber-400/20', next: 'closed', nextLabel: t('perfCycleManagerBtnClose'), nextIcon: Lock },
    closed: { label: t('perfCycleManagerStatusClosed'), color: 'text-content-tertiary bg-surface-secondary border-default' },
  };
  const [showModal, setShowModal] = useState(false);
  const [transitioning, setTransitioning] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleCreate = async (data: { name: string; cycle_type: string; start_date: string; end_date: string }) => {
    const cycle = await createCycle(data);
    setActiveCycleId(cycle.id);
  };

  const handleTransition = async (cycleId: string, newStatus: string) => {
    setTransitioning(cycleId);
    try {
      await updateCycleStatus(cycleId, newStatus);
    } finally {
      setTransitioning(null);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-content flex items-center gap-2">
          <CalendarRange size={18} className="text-primary" />
          {t('perfCycleManagerTitle')}
        </h3>
        <Button
          variant="success"
          size="md"
          leftIcon={<Plus size={14} />}
          onClick={() => setShowModal(true)}
        >
          {t('perfCycleManagerBtnNewCycle')}
        </Button>
      </div>

      {/* Explanation for first-time users */}
      {cycles.length === 0 && (
        <div className="p-4 rounded-xl bg-brand/[0.04] border border-brand/15">
          <p className="text-sm text-content-tertiary leading-relaxed">
            <strong className="text-content-secondary">{t('perfCycleManagerWhatsACycleTitle')}</strong> {t('perfCycleManagerWhatsACycleDesc')}
          </p>
        </div>
      )}

      {/* Cycle List */}
      <div className="space-y-2">
        {cycles.map(cycle => {
          const cfg = STATUS_CONFIG[cycle.status] || STATUS_CONFIG.draft;
          const isTransitioning = transitioning === cycle.id;

          return (
            <div key={cycle.id} className="flex items-center gap-4 p-4 rounded-xl bg-surface-primary border border-subtle hover:border-default transition-colors">
              <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <CalendarRange size={18} className="text-brand/60" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-content">{cycle.name}</span>
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium border ${cfg.color}`}>
                    {cfg.label}
                  </span>
                  {cycle.cycle_type !== 'custom' && (
                    <Repeat size={10} className="text-status-success/40" title={t('perfCycleManagerRecurringCycleTooltip')} />
                  )}
                </div>
                <div className="text-xs text-content-tertiary mt-0.5">
                  {new Date(cycle.start_date).toLocaleDateString()} — {new Date(cycle.end_date).toLocaleDateString()}
                  <span className="ml-2 text-content-muted">
                    {t(`perfCycleType${cycle.cycle_type.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')}` as 'perfCycleTypeCustom')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {cfg.next && cfg.nextIcon && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTransition(cycle.id, cfg.next!)}
                    loading={isTransitioning}
                    leftIcon={!isTransitioning ? <cfg.nextIcon size={12} /> : undefined}
                    className="text-primary bg-brand/10 hover:bg-brand/20 border-brand/15"
                  >
                    {cfg.nextLabel}
                  </Button>
                )}
                {confirmDeleteId === cycle.id ? (
                  <div className="flex items-center gap-1.5 animate-fade-in">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        setDeleteError(null);
                        try {
                          await deleteCycle(cycle.id);
                          setConfirmDeleteId(null);
                        } catch (err: any) {
                          setDeleteError(err?.response?.data?.detail || t('perfCycleManagerErrorCannotDelete'));
                        }
                      }}
                    >
                      {t('perfCycleManagerBtnDelete')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setConfirmDeleteId(null); setDeleteError(null); }}
                    >
                      {t('perfCycleManagerBtnNo')}
                    </Button>
                  </div>
                ) : (
                  <IconButton
                    icon={<Trash2 size={13} />}
                    variant="danger"
                    aria-label={t('perfCycleManagerAriaLabelDeleteCycle')}
                    onClick={() => setConfirmDeleteId(cycle.id)}
                  />
                )}
              </div>
            </div>
          );
        })}

        {cycles.length === 0 && (
          <div className="text-center py-12">
            <CalendarRange size={28} className="text-content-faint mx-auto mb-3" />
            <p className="text-sm text-content-tertiary mb-1">{t('perfCycleManagerNoCyclesTitle')}</p>
            <p className="text-xs text-content-muted">{t('perfCycleManagerNoCyclesDesc')}</p>
          </div>
        )}

        {deleteError && (
          <p className="text-xs text-status-error mt-2">{deleteError}</p>
        )}
      </div>

      {/* Create Cycle Modal */}
      <CycleModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        existingCycles={cycles}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default React.memo(CycleManager);
